import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { EASE, Reveal } from "@/components/aklive/motion";
import { PlaceholderBlock } from "@/components/aklive/PlaceholderBlock";
import { Lightbox } from "@/components/aklive/work/Lightbox";

export const FILTERS = ["All", "Branding", "Web", "Presentations", "Content", "Packaging", "Events"];

export const PROJECTS = [
  { slug: "proof-of-talk", name: "Proof of Talk", category: "Events", label: "[EVENT BRANDING — 21:9]", squareLabel: "[EVENT BRANDING — 4:5]", lightboxLabel: "[EVENT BRANDING — PROJECT ASSET]", wide: true },
  { slug: "midas-capital", name: "Midas Capital", category: "Branding", label: "[BRAND SYSTEM — 4:5]", squareLabel: "[BRAND SYSTEM — 4:5]", lightboxLabel: "[BRAND SYSTEM — PROJECT ASSET]", wide: false },
  { slug: "vnx", name: "VNX", category: "Web", label: "[WEBSITE MOCKUP — 4:5]", squareLabel: "[WEBSITE MOCKUP — 4:5]", lightboxLabel: "[WEBSITE MOCKUP — PROJECT ASSET]", wide: false },
  { slug: "puretive", name: "Puretive", category: "Packaging", label: "[PACKAGING CLOSE-UP — 21:9]", squareLabel: "[PACKAGING CLOSE-UP — 4:5]", lightboxLabel: "[PACKAGING CLOSE-UP — PROJECT ASSET]", wide: true },
  { slug: "salted-social", name: "Salted Social", category: "Content", label: "[CONTENT SYSTEM — 4:5]", squareLabel: "[CONTENT SYSTEM — 4:5]", lightboxLabel: "[CONTENT SYSTEM — PROJECT ASSET]", wide: false },
  { slug: "meducan", name: "Meducan", category: "Presentations", label: "[PITCH DECK SPREAD — 4:5]", squareLabel: "[PITCH DECK SPREAD — 4:5]", lightboxLabel: "[PITCH DECK SPREAD — PROJECT ASSET]", wide: false },
];

const Meta = ({ project }) => (
  <div className="mt-5">
    <h3 className="font-display text-2xl font-bold tracking-tight text-ink transition-colors duration-300 group-hover:text-ember md:text-3xl">
      {project.name}
    </h3>
    <dl className="mt-3 space-y-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink2">
      <div className="flex gap-3">
        <dt>Category</dt>
        <dd>{project.category}</dd>
      </div>
      <div className="flex gap-3">
        <dt>Industry</dt>
        <dd>[INDUSTRY]</dd>
      </div>
    </dl>
  </div>
);

const WorkItem = ({ project, ratio, label, onOpen }) => (
  <article className="group" data-testid={`work-item-${project.slug}`}>
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View ${project.name} — ${project.category}`}
      data-testid={`work-item-btn-${project.slug}`}
      className="block w-full overflow-hidden text-left"
    >
      <PlaceholderBlock
        label={label}
        ratio={ratio}
        className="transition-transform duration-500 ease-out group-hover:scale-[1.02]"
      />
    </button>
    <Meta project={project} />
  </article>
);

export const WorkGallery = () => {
  const reduce = useReducedMotion();
  const [filter, setFilter] = useState("All");
  const [openIndex, setOpenIndex] = useState(null);

  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);
  const openAt = (project) => setOpenIndex(visible.findIndex((p) => p.slug === project.slug));

  const item = (project, wideRow) => (
    <WorkItem
      key={project.slug}
      project={project}
      ratio={wideRow ? "21 / 9" : "4 / 5"}
      label={wideRow ? project.label : project.squareLabel}
      onOpen={() => openAt(project)}
    />
  );

  return (
    <section className="py-16 md:py-24" data-testid="work-gallery-section">
      <div className="container-ak">
        <Reveal>
          <div className="no-scrollbar -mx-5 overflow-x-auto px-5 sm:mx-0 sm:overflow-visible sm:px-0">
            <div className="flex gap-7 border-b border-line md:gap-10" role="group" aria-label="Filter work by category">
              {FILTERS.map((f) => {
                const active = filter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={active}
                    data-testid={`filter-btn-${f.toLowerCase()}`}
                    onClick={() => setFilter(f)}
                    className={`relative flex min-h-[44px] items-center whitespace-nowrap text-sm tracking-wide transition-colors duration-300 ${
                      active ? "text-ink" : "text-ink2 hover:text-ink"
                    }`}
                  >
                    {f}
                    {active &&
                      (reduce ? (
                        <span className="absolute bottom-0 left-0 h-[2px] w-full bg-ember" />
                      ) : (
                        <motion.span layoutId="work-filter-underline" className="absolute bottom-0 left-0 h-[2px] w-full bg-ember" />
                      ))}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>
        <p className="sr-only" aria-live="polite">
          {visible.length} {visible.length === 1 ? "project" : "projects"} shown
        </p>

        <div className="mt-12 min-h-[60vh] md:mt-16">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={filter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.25, ease: EASE }}
            >
              {filter === "All" ? (
                <div className="space-y-16 md:space-y-24">
                  {item(PROJECTS[0], true)}
                  <div className="grid grid-cols-1 gap-16 md:grid-cols-2 md:gap-10 lg:gap-16">
                    {item(PROJECTS[1], false)}
                    {item(PROJECTS[2], false)}
                  </div>
                  {item(PROJECTS[3], true)}
                  <div className="grid grid-cols-1 gap-16 md:grid-cols-2 md:gap-10 lg:gap-16">
                    {item(PROJECTS[4], false)}
                    {item(PROJECTS[5], false)}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-16 md:grid-cols-2 md:gap-10 lg:gap-16">
                  {visible.map((p) => item(p, false))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {openIndex !== null && (
          <Lightbox
            items={visible}
            index={openIndex}
            onNavigate={(dir) => setOpenIndex((i) => (i + dir + visible.length) % visible.length)}
            onClose={() => setOpenIndex(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
